import { useMemo, useState, useEffect, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import { contentItems, typeLabels } from "@/mocks/content";
import { useSeo } from "@/hooks/useSeo";
import HeroCarousel from "./components/HeroCarousel";
import CategoryEntries from "./components/CategoryEntries";
import RecommendList from "./components/RecommendList";
import NewsTicker from "./components/NewsTicker";
import TopicGrid from "./components/TopicGrid";

export default function Home() {
  useSeo({
    title: "快猫 - 多元娱乐内容与最新资讯聚合平台",
    description:
      "快猫网站提供丰富的娱乐内容覆盖影视、音乐、动态资讯等多个领域，满足用户多样化的娱乐需求，打造全面的品牌内容体验。",
    keywords: "快猫,娱乐内容,影视资讯,音乐推荐,动态新闻,品牌娱乐平台,娱乐资讯平台",
  });

  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 500);
    return () => clearTimeout(timer);
  }, []);

  const data = useMemo(() => {
    const films = contentItems
      .filter((i) => i.type === "film")
      .sort((a, b) => b.heat - a.heat);
    const music = contentItems
      .filter((i) => i.type === "music")
      .sort((a, b) => b.heat - a.heat);
    const news = contentItems
      .filter((i) => i.type === "news")
      .sort((a, b) => (a.date < b.date ? 1 : -1));
    const hot = [...contentItems].sort((a, b) => b.heat - a.heat).slice(0, 6);
    const carousel = [
      films[0],
      music[0],
      films[1],
      news[0],
    ].filter(Boolean);
    return { films, music, news, hot, carousel };
  }, []);

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const kw = keyword.trim();
    if (!kw) return;
    navigate(`/search?q=${encodeURIComponent(kw)}`);
  };

  return (
    <SiteLayout>
      <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-8 md:py-8">
        <section className="w-full rounded-lg bg-background-100 px-5 py-8 md:px-10 md:py-10">
          <div className="flex flex-col items-center text-center">
            <span className="flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 font-label text-xs font-semibold text-primary-700">
              <i className="ri-fire-fill" />
              多元娱乐内容与最新资讯聚合平台
            </span>
            <h1 className="mt-4 font-heading text-2xl font-extrabold text-foreground-950 md:text-4xl">
              发现值得一看一听的精彩内容
            </h1>
            <p className="mt-3 max-w-2xl text-sm text-foreground-600 md:text-base">
              快猫聚合影视资讯、音乐推荐与动态娱乐新闻，帮你快速掌握最新娱乐动态。
            </p>
            <form onSubmit={submitSearch} className="mt-6 flex w-full max-w-xl flex-col gap-3 sm:flex-row">
              <div className="relative flex-1">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400" />
                <input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="输入关键词，搜索影视、音乐或资讯"
                  className="w-full rounded-full border border-background-300/70 bg-background-50 py-3 pl-11 pr-4 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
                />
              </div>
              <button
                type="submit"
                className="whitespace-nowrap rounded-full bg-primary-500 px-7 py-3 font-label text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                搜索
              </button>
            </form>
          </div>
        </section>

        {loading ? (
          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="h-72 animate-pulse rounded-lg bg-background-100 lg:col-span-2 md:h-[440px]" />
            <div className="h-72 animate-pulse rounded-lg bg-background-100 md:h-[440px]" />
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <HeroCarousel items={data.carousel} />
            </div>
            <div className="rounded-lg border border-background-200/80 bg-background-50 p-5">
              <NewsTicker items={data.news.slice(0, 5)} />
            </div>
          </div>
        )}

        <div className="mt-12">
          <CategoryEntries />
        </div>

        <div className="mt-12 grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="flex flex-col gap-10 lg:col-span-2">
            <RecommendList
              title="最新影视推荐"
              subtitle="热映与口碑佳作，一次看个够"
              items={data.films.slice(0, 5)}
              moreTo="/category/film"
            />
            <RecommendList
              title="音乐新曲推介"
              subtitle="流行、民谣、电子，总有一首适合此刻的你"
              items={data.music.slice(0, 5)}
              moreTo="/category/music"
            />
          </div>

          <aside className="flex flex-col gap-6">
            <div className="rounded-lg border border-background-200/80 bg-background-50 p-5">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="font-heading text-lg font-extrabold text-foreground-950">
                  <i className="ri-fire-line mr-1 text-primary-500" />
                  热门榜单
                </h2>
                <Link to="/category" className="text-xs font-medium text-accent-600 hover:text-accent-700">
                  全部
                </Link>
              </div>
              <ul className="space-y-3">
                {data.hot.map((item, index) => (
                  <li key={item.id}>
                    <Link to={`/detail/${item.id}`} className="group flex items-center gap-3">
                      <span
                        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded font-label text-xs font-bold ${
                          index < 3
                            ? "bg-primary-500 text-background-50"
                            : "bg-background-100 text-foreground-500"
                        }`}
                      >
                        {index + 1}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="line-clamp-1 text-sm font-medium text-foreground-900 group-hover:text-primary-600">
                          {item.title}
                        </p>
                        <p className="mt-0.5 text-xs text-foreground-500">{typeLabels[item.type]}</p>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg border border-background-200/80 bg-secondary-100/60 p-5">
              <h2 className="font-heading text-lg font-extrabold text-foreground-950">订阅快猫</h2>
              <p className="mt-2 text-sm text-foreground-600">
                想第一时间收到精选推荐？前往关于我们页面留下你的联系方式。
              </p>
              <Link
                to="/about"
                className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-secondary-500 px-5 py-2.5 font-label text-sm font-semibold text-background-50 transition-colors hover:bg-secondary-600"
              >
                <i className="ri-mail-send-line" />
                联系与反馈
              </Link>
            </div>
          </aside>
        </div>

        <div className="mt-12">
          <TopicGrid />
        </div>
      </div>
    </SiteLayout>
  );
}